import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CreativeTypes } from '@bynder-studio/render-core';
import {
    collectAlertMessagesFromDesignList,
    getExportableDesignsFromDesignList,
    getUniqueCreativeTypesFromDesignList,
} from './utils';
import { type ExportResult } from './types';
import { type RenditionConfiguration } from '~/services/RenditionsService';
import { PresetData } from './Preset';
import { useFlyInNotificationContext } from 'packages/FlyInNotifications/FlyInNotificationsContext';
import { type ExportPayload } from 'packages/types/exports';
import { ExportPresetValue } from 'packages/hooks/useExportPreset';
import CreativesService from '~/services/CreativesService';
import { isAbortError } from 'packages/helpers/helpers';
import { sendAppcuesEvent } from '~/helpers/RouteWithAppcues';
import { Creative } from 'packages/store/creatives/types';
import useAccessRights from 'packages/hooks/useAccessRights';

function useDesignsBulkExportModal(designs: Creative[], isOpen: boolean, onClose: () => void) {
    const { isBynderScopeAllowed } = useAccessRights();
    const exportableDesigns = useMemo(() => getExportableDesignsFromDesignList(designs), [designs]);
    const creativeTypes = useMemo(() => getUniqueCreativeTypesFromDesignList(designs), [designs]);
    const [isLoadingRenditions, setIsLoadingRenditions] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [exportResults, setExportResults] = useState<ExportResult[]>([]);
    const alertMessage = useMemo(
        () => collectAlertMessagesFromDesignList(designs, exportResults),
        [designs, exportResults],
    );
    const selectedProfiles = useRef<Record<CreativeTypes, RenditionConfiguration> | undefined>(undefined);
    const selectedPresetData = useRef<PresetData>(null);
    const { showNotification } = useFlyInNotificationContext();

    const isDownloadBlocked = useMemo(
        () =>
            exportableDesigns.some(
                (design) =>
                    design.exportIntegrations?.find((item) => item?.channelType === 'DOWNLOAD')?.enabled === false,
            ),
        [exportableDesigns],
    );

    const isWaitingRoomBlocked = useMemo(
        () =>
            !isBynderScopeAllowed(['asset:write']) ||
            exportableDesigns.some(
                (design) =>
                    design.exportIntegrations?.find((item) => item?.channelType === 'BYNDER')?.enabled === false,
            ),
        [isBynderScopeAllowed, exportableDesigns],
    );

    const handleRenditionsLoad = useCallback(() => {
        setIsLoadingRenditions(false);
    }, []);

    const handleSelectProfile = useCallback((profiles: Record<CreativeTypes, RenditionConfiguration>) => {
        selectedProfiles.current = profiles;
    }, []);

    const handleSelectPreset = useCallback((presetData: PresetData) => {
        selectedPresetData.current = presetData;
    }, []);

    const handleExport = useCallback(() => {
        if (!selectedPresetData.current || !selectedProfiles.current) {
            return;
        }

        setIsExporting(true);
        const { preset, bynderAccountId } = selectedPresetData.current;

        Promise.all(
            exportableDesigns.map((design) => {
                const selectedProfile = selectedProfiles.current[design.creativeType];

                if (!selectedProfile) {
                    return null;
                }

                let payload: ExportPayload = {
                    name: preset.label,
                    type: preset.value,
                    variationIds: design.variationIds,
                };

                if (preset.value === ExportPresetValue.BYNDER) {
                    payload = {
                        ...payload,
                        renditionConfigurationId: selectedProfile.id,
                        bynderAccountId,
                    };
                } else if (preset.value === ExportPresetValue.DOWNLOAD) {
                    payload = {
                        ...payload,
                        renditionConfigurationId: selectedProfile.id,
                    };
                }

                return CreativesService.createExportJob(design.id, payload)
                    .then(({ json, status }) => {
                        if (status === 200) {
                            const notificationId = json.id;

                            showNotification({
                                notificationId,
                                show: true,
                                creativeName: design.name,
                                thumbnailUrl: design.thumbnailImage,
                                exportType: preset.value,
                            });

                            return {
                                data: json,
                                success: true,
                                designId: design.id,
                            } as ExportResult;
                        }

                        return {
                            ...(json as unknown as { message: string }),
                            success: false,
                            designId: design.id,
                        } as ExportResult;
                    })
                    .catch((err) => {
                        if (isAbortError(err)) {
                            return null;
                        }

                        return {
                            message: err.message,
                            success: false,
                            designId: design.id,
                        } as ExportResult;
                    });
            }),
        )
            .then((result) => {
                const filteredResult = result.filter((r): r is ExportResult => r !== null);

                if (filteredResult.length === exportableDesigns.length && filteredResult.every((r) => r.success)) {
                    sendAppcuesEvent('Bulk export job created', {
                        designIds: exportableDesigns.map((d) => d.id),
                        image: selectedProfiles.current[CreativeTypes.IMAGE]?.displayName,
                        video: selectedProfiles.current[CreativeTypes.VIDEO]?.displayName,
                        name: preset.label,
                        type: preset.value,
                    });

                    onClose();
                } else {
                    setExportResults(filteredResult);
                }
            })
            .finally(() => {
                setIsExporting(false);
            });
    }, [exportableDesigns, onClose]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        return () => {
            setIsLoadingRenditions(true);
            setIsExporting(false);
        };
    }, [isOpen]);

    return {
        handleExport,
        isExporting,
        isLoadingRenditions,
        isDownloadBlocked,
        isWaitingRoomBlocked,
        exportableDesignsLength: exportableDesigns.length,
        exportResults,
        creativeTypes,
        alertMessage,
        handleSelectPreset,
        handleRenditionsLoad,
        handleSelectProfile,
    };
}

export default useDesignsBulkExportModal;
